/**
 * Premortem Letter J — OCR sanity flags banner on the timesheet review card.
 *
 * Renders right above the entries table whenever the timesheet has a
 * non-empty sanity_report. Severity-tinted (info/warning/critical),
 * lists each flag with a short message, and surfaces a "Manually
 * approve" affordance for supervisors when a CRITICAL flag is blocking
 * downstream claim creation.
 *
 * Why a dedicated banner vs. inline per-row badges?
 *   - Many flags (per-week total, declared-vs-computed mismatch) are
 *     timesheet-wide, not row-bound. A banner makes the supervisor
 *     read the whole story before clicking into any single row.
 *   - When a critical flag exists, the supervisor MUST make a
 *     conscious "I see this; I am still approving" gesture. A banner
 *     with a prominent button is the right scale for that gesture.
 */
import React, { useState } from 'react';
import { formatDateTime } from '../../lib/utils';
import { WarningOctagon, Warning, Info, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../services/api';

const SEVERITY_STYLES = {
  critical: {
    wrapper: 'bg-rose-50 border-rose-300 text-rose-900',
    icon: WarningOctagon,
    iconCls: 'text-rose-600',
    title: 'OCR sanity check flagged this timesheet',
  },
  warning: {
    wrapper: 'bg-amber-50 border-amber-300 text-amber-900',
    icon: Warning,
    iconCls: 'text-amber-600',
    title: 'Sanity warnings — review before submitting',
  },
  info: {
    wrapper: 'bg-slate-50 border-slate-200 text-slate-800',
    icon: Info,
    iconCls: 'text-slate-500',
    title: 'Sanity report',
  },
};

const FlagBadge = ({ flag }) => {
  const sev = SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.info;
  const Icon = sev.icon;
  return (
    <li
      className="flex items-start gap-2 py-1.5 text-sm"
      data-testid={`sanity-flag-${flag.code}`}
    >
      <Icon size={16} className={`flex-shrink-0 mt-0.5 ${sev.iconCls}`} />
      <div className="flex-1">
        <div className="font-medium">{flag.message}</div>
        {(flag.observed || flag.expected) && (
          <div className="text-xs opacity-75 mt-0.5 font-mono">
            observed: {flag.observed ?? 'n/a'}
            {flag.expected && ` · expected: ${flag.expected}`}
          </div>
        )}
      </div>
    </li>
  );
};

export const SanityFlagsBanner = ({
  timesheetId,
  sanityReport,
  override,
  expectedConfirmToken,
  onOverrideRecorded,
}) => {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!sanityReport || !sanityReport.flags || sanityReport.flags.length === 0) {
    return null;
  }

  const sev = SEVERITY_STYLES[sanityReport.severity] || SEVERITY_STYLES.info;
  const Icon = sev.icon;
  const hasCritical = sanityReport.has_critical;

  const handleOverride = async () => {
    if (reason.trim().length < 10) {
      toast.error('Please describe why (at least 10 characters).');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/timesheets/${timesheetId}/sanity-override`,
        { reason: reason.trim(), confirm_token: expectedConfirmToken },
      );
      toast.success('Sanity override recorded.');
      setOverrideOpen(false);
      setReason('');
      if (onOverrideRecorded) onOverrideRecorded(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Override failed: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-3 ${sev.wrapper}`}
      data-testid={`sanity-banner-${sanityReport.severity}`}
      role="alert"
    >
      <div className="flex items-start gap-3">
        <Icon size={22} weight="fill" className={`flex-shrink-0 mt-0.5 ${sev.iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base">{sev.title}</div>
          {override ? (
            <div className="text-xs mt-1 opacity-90">
              Manually approved by{' '}
              <span className="font-mono">{override.by_user_email}</span>{' '}
              on {formatDateTime(override.overridden_at)}:{' '}
              <em>&ldquo;{override.reason}&rdquo;</em>
            </div>
          ) : (
            <div className="text-xs mt-1 opacity-90">
              {sanityReport.critical_count > 0 &&
                `${sanityReport.critical_count} critical · `}
              {sanityReport.warning_count > 0 &&
                `${sanityReport.warning_count} warning · `}
              {hasCritical && (
                <span className="font-medium">
                  claim creation is blocked until a supervisor overrides.
                </span>
              )}
            </div>
          )}
          <ul className="mt-2 divide-y divide-current/10">
            {sanityReport.flags.map((f, i) => (
              <FlagBadge key={`${f.code}-${i}`} flag={f} />
            ))}
          </ul>
        </div>
        {hasCritical && !override && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOverrideOpen(true)}
            data-testid="sanity-banner-override-btn"
          >
            Manually approve
          </Button>
        )}
      </div>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent data-testid="sanity-override-dialog">
          <DialogHeader>
            <DialogTitle>Override OCR sanity check</DialogTitle>
            <DialogDescription>
              Tell future-you (or an auditor) exactly why this anomaly is
              acceptable. Be specific — &ldquo;GPS log shows full shift&rdquo;
              or &ldquo;Confirmed with patient&rsquo;s family&rdquo;.
              This record is permanent and cannot be edited later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="sanity-override-reason">Reason for override</Label>
            <Textarea
              id="sanity-override-reason"
              data-testid="sanity-override-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="e.g. GPS log confirms full 15-hour overnight shift; patient family verified."
              disabled={submitting}
            />
            <div className="text-xs text-slate-500">
              {reason.trim().length}/10+ characters
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOverrideOpen(false)}
              disabled={submitting}
            >
              <X size={16} className="mr-1.5" />
              Cancel
            </Button>
            <Button
              onClick={handleOverride}
              disabled={submitting || reason.trim().length < 10}
              data-testid="sanity-override-confirm-btn"
            >
              {submitting ? 'Recording…' : 'Record override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SanityFlagsBanner;
